import {html, render} from 'lit';
import {Module, PubSub, Viewport} from '@mamdev/cad.static-next-lib';
import {HistoryService} from '../../ts/services/history';
import UrlPath from '../../ts/services/url-path';
import {
  BreakType,
  type EventClickName,
  type Seconds,
  type Slide,
  SlideType,
  type TrackingClickName
} from '@mamdev/cad.static-next-lib';

import {VideoFeedMetric} from '@mamdev/cad.static-next-lib';
import {VideoFeedModel} from '@mamdev/cad.static-next-lib';
import {VideoFeedTracking} from '@mamdev/cad.static-next-lib';
import {classMap} from 'lit/directives/class-map.js';
import {
  consoleError,
  consoleLog,
  consoleWarn,
  formatTime, getSlideIndexFromAdContainer,
  isDesktop,
  elementContentOverflows, clamp, isMobile
} from './utils';

const logError = (e: unknown) => {
  consoleError(e);
  PubSub.publish('sentry:log', {error: e, module: 'video-slideshow'});
};

const errorClass = 'error';

// Ensure there is only one instance at most.
let videoSlideshowInstances = 0;

/**
 * Provides an "endless" video view functionality.
 * Users can swipe through videos one at a time and more videos will be loaded until there's no more left.
 */
export default class VideoSlideshow extends Module {
  private readonly slidesBetweenAds = 4;

  // Whether to show the overlay or not.
  private shown = false;

  private _paused = false;
  private _muted = false;

  // Observes slides coming into view for managing the various players playback, tracking, etc.
  private observer: IntersectionObserver;

  // True if the module is to be displayed inline, instead of as an overlay.
  private isInlineMode = false;

  private get isOverlayMode() {
    return !this.isInlineMode;
  }

  // True if we are in a standalone page on mobile, which opens the overlay instead of being displayed inline.
  private isStandaloneMobilePage = false;

  // Whether the module is being loaded in a prototype page or not
  private isPrototype = false;

  private autoplay = true;

  // Used to display an ad slide only for a certain amount of time;
  private readonly adSlideDuration: Seconds = 15;
  private adSlideTimer: number;
  private forceVideoToPlayTimer: number;

  private pausedByUser = false;

  // Keep track of whether the player was playing or not before pausing, to restart it when switching tabs.
  private playerPlayingBeforePausing = false;

  // To display the play icon in inline mode since autoplay is disabled there.
  private firstPlayStarted = false;

  // Used to hide the slide preview image once the video is visible, to prevent visual artifacts.
  private firstVideoFrameAvailable = false;

  // Peek effect to show the users they can scroll vertically to the next slide.
  private hasPeeked = false;

  // Premium customers won't see ad slides.
  private isPremiumUser = false;

  public model: VideoFeedModel;
  private readonly tracking: VideoFeedTracking;
  private readonly metric: VideoFeedMetric;

  // closes the overlay if the back button is pressed, which prevents the page from closing on android
  private readonly popStateHandler = (event: Event) => {
    event.preventDefault();
    this.closeOverlay();
  };

  private initialSlideElement: HTMLElement | null;
  private initialPlayerContainer: HTMLElement | null;
  private video: HTMLVideoElement;
  private _playerContainer: HTMLElement | null;
  private _progressBar: HTMLElement | null;

  // Keep track of the page scroll amount to revert to it when closing the layer, since the iOS fix changes it.
  private pageScrollTop: number;

  // Video position when the progress bar is being hovered, used to update the display of various UI elements.
  private progressBarHoverVideoTime: number;
  private progressBarHoverOffsetX: number;

  private get playerContainer() {
    this._playerContainer ??= this.root_.querySelector<HTMLElement>('.video-slideshow__overlay .video-slideshow__player-container');

    return this._playerContainer;
  }

  private get progressBar() {
    this._progressBar ??= this.root_.querySelector<HTMLElement>('.video-slideshow__progress-bar');

    return this._progressBar;
  }

  private get slideElements() {
    return this.root_.querySelectorAll<HTMLElement>('.video-slideshow__slide');
  }

  private get firstSlideElement() {
    return this.root_.querySelector('.video-slideshow__slide');
  }

  private get currentSlideElement() {
    return this.slideElements[this.model.currentSlideIndex];
  }

  private get nextSlideElement() {
    return this.slideElements[this.model.currentSlideIndex + 1];
  }

  private get prevSlideElement() {
    return this.slideElements[this.model.currentSlideIndex - 1];
  }

  private get muted() {
    return this._muted;
  }

  private set muted(val: boolean) {
    this._muted = val;

    this.video.muted = val;

    this.updateView();
    this.sendTrackingPixel(this.video.muted ? 'video-mute' : 'video-unmute', 'volume-icon');
  }

  private get paused() {
    return this._paused;
  }

  private set paused(val: boolean) {
    this._paused = val;

    this.updateView();
  }

  private readonly blurHandler = () => {
    // consoleLog('blur ', event);
    // consoleLog('blur ', this.currentSlide);
    this.playerPlayingBeforePausing = !this.video.paused;
    this.video.pause();
    this.pausedByUser = true;
  };

  private readonly focusHandler = () => {
    // consoleLog('focus ', event);
    if (this.autoplay && this.model.currentSlide.type !== SlideType.break && this.playerPlayingBeforePausing) {
      // consoleLog('focus ', this.currentSlide);
      void this.playVideo();
    }
  };

  private readonly resizeHandler = () => {
    this.positionPlayerOnTopOfCurrentSlide();
  };

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'video-slideshow';

    this.metric = new VideoFeedMetric('');
    this.tracking = new VideoFeedTracking();
  }

  static attachTo(root: HTMLElement): VideoSlideshow {
    return new VideoSlideshow(root);
  }

  start() {
    try {
      consoleLog('start');

      if (videoSlideshowInstances > 0) return;

      videoSlideshowInstances = 1;

      this.isInlineMode = this.root_.dataset.mode === 'inline' && !isMobile();
      this.isStandaloneMobilePage = this.root_.dataset.mode === 'inline' && isMobile();
      this.isPrototype = 'prototype' in this.root_.dataset;
      this.isPremiumUser = !window.ui.adServiceEnabled;
      this.autoplay = this.isOverlayMode;
      this.paused = !this.autoplay;

      consoleLog('isPremiumUser', this.isPremiumUser);
      consoleLog('isPrototype', this.isPrototype);
      consoleLog('isInline', this.isInlineMode);
      consoleLog('autoplay', this.autoplay);
      consoleLog('isDesktop', isDesktop());
      consoleLog('isAndroidApp', Viewport.isAndroidApp());
      consoleLog('isIOSApp', Viewport.isIOSApp());

      this.setupEventListeners();

      if (this.isInlineMode) {
        this.initialSlideElement = this.root_.querySelector<HTMLElement>('.video-slideshow__overlay-bottom');
        this.initialPlayerContainer = this.root_.querySelector<HTMLElement>('.video-slideshow__overlay .video-slideshow__player-container');
        this.openSlideshow(0, [window.ui.pageinfo.coremid]);

        // Disable onboarding in inline mode.
        this.hasPeeked = true;
      } else if (this.isStandaloneMobilePage) {
        this.root_.classList.add('showPlayIcon');
      }

      if (window.AdService) {
        this.setUpAdServiceCommunication();
      } else {
        this.subscribe('ad:adServiceReady', () => {
          this.setUpAdServiceCommunication();
        });
      }

      PubSub.publish('video-slideshow:initialized');
    } catch (e) {
      logError(e);
    }
  }

  private updateView() {
    render(this.renderSlideshow(), this.root_);
  }

  private async loadNextSlides() {
    consoleLog('load next slides');

    return this.model.fetchNextSlides().then(() => {
      try {
        consoleLog('slides loaded', this.model.slides);

        this.updateView();
        this.observeSlides();

        // Delay requesting ads to avoid chocking up the connection.
        setTimeout(() => {
          try {
            // Set up correct AdService parameters
            let section = 'overview';

            if (window.ui.AdServiceConfig.pageCategory === 'homepage') {
              section = 'homepage';
            } else if (this.isInlineMode) {
              section = 'content';
            }

            window.AdService?.setParam('section', section);

            const notInitializedAdContainers = this.root_.querySelectorAll<HTMLElement>('.co-container:not([data-service-slot-initialized])');

            this.publish('dynamicAdsNeeded', notInitializedAdContainers);

            notInitializedAdContainers.forEach(adContainer => {
              const slotId = adContainer.getAttribute('data-service-slot');

              if (slotId) {
                window.AdService?.AdSlot(slotId).setConfig('visibility-lookahead', window.screen.height * 3);
              }
            });
          } catch (e) {
            logError(e);
          }
        }, 2000);
      } catch (e) {
        logError(e);
      }
    }).catch(e => {
      throw e;
    });
  }

  private setUpIntersectionObserver() {
    consoleLog('setup intersection observer');

    if (this.observer !== undefined) return;

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (!entry.target) return;

        const slide = entry.target as HTMLElement;
        const slideIndex = parseInt(slide.dataset.index ?? '0');

        // consoleLog('slide intersected', slideIndex, entry.target);

        const model = this.model;

        if (model.currentSlideIndex === slideIndex) return;

        const previousSlideIndex = model.currentSlideIndex;
        const previousSlideElement = this.slideElements[previousSlideIndex];

        model.goToSlide(slideIndex);

        const currentSlide = model.currentSlide;
        const isVideoSlide = model.isVideoSlide(currentSlide);

        if (isVideoSlide) this.metric.markSlideActive();

        // consoleLog('current slide index', this.currentSlideIndex);

        // Remove error classes
        this.playerContainer?.classList.remove(errorClass);

        // Temporarily hide the player to avoid visual issues.
        this.playerContainer?.classList.add('hidden');

        const previousProgressIndicator = previousSlideElement.querySelector<HTMLElement>('.video-slideshow__progress-indicator');
        if (previousProgressIndicator) previousProgressIndicator.style.width = '0';

        // Reset slide-related flags and timers.
        window.clearTimeout(this.adSlideTimer);

        // The user at this point knows that vertical scrolling is possible, no need for a peek animation anymore.
        this.hasPeeked = true;
        this.pausedByUser = false;

        if (isVideoSlide) {
          this.firstVideoFrameAvailable = false;
          this.video.poster = currentSlide.pictureUrl;
          this.video.src = currentSlide.videoUrls[1280];

          this.positionPlayerOnTopOfCurrentSlide();
          void this.playVideo('video');

          this.updateHistoryState();
        } else {
          this.video.currentTime = 0;
          this.video.pause();

          this.adSlideTimer = window.setTimeout((index: number) => {
            // consoleLog('ad slide timer end');
            if (index === slideIndex && currentSlide.breakType !== BreakType.video) {
              this.scrollToNextSlide();
            }
          }, this.adSlideDuration * 1000, model.currentSlideIndex);
        }

        this.updateView();

        const event = slideIndex > previousSlideIndex ? 'video-slideshow-next' : 'video-slideshow-prev';

        this.tracking.sendPI(event, currentSlide, slideIndex);
        this.tracking.sendTracking2EventPixel(event, this.model.currentSlide);
        // this.sendEventPixel(event);

        // Load more slides in case this is the last slide. Necessary here in case the overlay opens from the last teaser.
        if ('last' in slide.dataset) {
          consoleLog('intersected with last slide, loading next slides...');
          void this.loadNextSlides();
        }

        this.publish('welcomeback:restartWBTimer');
      });
    }, {
      threshold: 0.5
    });
  }

  private setupEventListeners() {
    consoleLog('set up event listeners');

    PubSub.subscribe('vertical-video:teaser-clicked', data => {
      // consoleLog('teaser-clicked', data);
      if (!data) return;
      if (typeof data !== 'object') return;
      if (!('targetElement' in data) || !('videoIDs' in data)) return;

      try {
        this.metric.markTeaserClicked();

        const index = parseInt((data.targetElement as HTMLElement).dataset.index ?? '');

        this.openSlideshow(index, data.videoIDs as number[]);
      } catch (e) {
        logError(e);
        this.closeOverlay();
      }
    });

    if (this.isStandaloneMobilePage) {
      this.root_.querySelector('video')?.addEventListener('click', e => {
        e.preventDefault();
        this.openSlideshow(0, [window.ui.pageinfo.coremid]);
      });
    }
  }

  private openSlideshow(initialSlideIndex = 0, videoIDs: number[] = []) {
    // Reset variables.
    this.pausedByUser = false;

    let initialPage = 0;

    consoleLog('open slideshow', `index=${initialSlideIndex}`, videoIDs, `isPremiumUser=${this.isPremiumUser}`);

    if (this.root_.dataset.pagenumber) {
      initialPage = parseInt(this.root_.dataset.pagenumber);
    }

    this.model = new VideoFeedModel({
      backendUrl: this.root_.dataset.url ?? '',
      initialSlideIndex: initialSlideIndex,
      initialPage: initialPage,
      initialVideoIDs: videoIDs,
      isPremiumUser: this.isPremiumUser,
      isPrototype: this.isPrototype,
      slidesBetweenAds: this.slidesBetweenAds
    });

    this.shown = true;
    this.updateView();

    if (this.isOverlayMode) {
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this.closeOverlay();
      });
    }

    if (Viewport.isDesktop()) {
      window.addEventListener('resize', this.resizeHandler);
    }

    if (this.isOverlayMode) {
      window.history.pushState({}, '', document.location.href);
      window.addEventListener('popstate', this.popStateHandler);
    }

    if (Viewport.isAndroidApp()) {
      window.addEventListener('blur', this.blurHandler);
      window.addEventListener('focus', this.focusHandler);
    }

    document.documentElement.style.overflowY = 'hidden';

    // Fix for iOS: hide the whole page content except this module, and append it to the body to be able to use position:static.
    if (Viewport.isIOSApp()) {
      this.pageScrollTop = document.documentElement.scrollTop;

      const wrapperElement = document.querySelector<HTMLElement>('.wrapper');
      if (wrapperElement) wrapperElement.style.display = 'none';

      const infiniteScrollElement = document.querySelector<HTMLElement>('[data-mod-name="infinitescroll"]');
      if (infiniteScrollElement) infiniteScrollElement.style.display = 'none';

      const leftColumnElement = document.querySelector<HTMLElement>('aside.left-col');
      if (leftColumnElement) leftColumnElement.style.display = 'none';

      const headerElement = document.querySelector<HTMLElement>('[data-mod-name="header"]');
      if (headerElement) headerElement.style.display = 'none';

      document.documentElement.style.backgroundColor = 'black';
      document.body.appendChild(this.root_);
      this.root_.classList.add('ios');

      // window.screen.height - window.innerHeight should give us the real available viewport area.
      const uiBarHeight = window.screen.height - window.innerHeight - 10; // Some additional pixels required cause of course iOS reports wrong measures.
      const overlay = this.root_.querySelector<HTMLElement>('.video-slideshow__overlay');

      if (overlay) {
        overlay.style.height = `${window.innerHeight - uiBarHeight}px`;
      }

      // consoleLog('overlay height', overlay.style.height);
    }

    this.metric.markModuleLoaded();
    this.sendEventPixel('video-slideshow-open');
    this.setUpIntersectionObserver();

    this.loadNextSlides().then(() => {
      if (this.isInlineMode && this.initialPlayerContainer) this.initialPlayerContainer.remove();

      this.setUpVideoPlayer();

      consoleLog('real slide index', this.model.currentSlideIndex);
      consoleLog('current slide', this.model.currentSlide, this.currentSlideElement);

      if (this.initialSlideElement) this.initialSlideElement.remove();
      if (this.isInlineMode) this.root_.className = '';

      // Allows starting the slideshow from a specific slide index.
      this.currentSlideElement.scrollIntoView({
        behavior: 'instant'
      });

      if (this.autoplay) {
        void this.playVideo();
      }

      // Activate the observer only after the correct slide has been scrolled into view.
      this.observeSlides();

      this.tracking.sendPI('video-slideshow-next', this.model.currentSlide, this.model.currentSlideIndex);

      if ('last' in this.currentSlideElement.dataset) {
        void this.loadNextSlides();
      }

      if (this.isOverlayMode) {
        this.updateHistoryState();

        // Video can never autoplay unless the user interacted with the page first, which happens only in overlay mode.
        this.forceVideoToPlayTimer = window.setInterval(() => {
          if (this.model.isVideoSlide(this.model.currentSlide) && this.video.paused && !this.pausedByUser) {
            // consoleLog('forcing video to play');
            void this.playVideo();
          }
        }, 1000);
      }

      PubSub.subscribe('visibilityStateChanged', e => {
        const pageHidden = ((e as Event).target as HTMLElement).hidden;

        if (pageHidden) {
          this.playerPlayingBeforePausing = !this.video.paused;
          this.video.pause();
          this.pausedByUser = true;
        } else if (this.autoplay && this.model.isVideoSlide(this.model.currentSlide) && this.playerPlayingBeforePausing) {
          void this.playVideo();
        }
      });

      // Preload error icon.
      new Image().src = `${UrlPath.moduleAssets}/video-slideshow/icon-error.svg`;
    }).catch(e => {
      logError(e);
      this.closeOverlay();
    });
  }

  private setUpAdServiceCommunication() {
    // consoleLog('setting up AdService communication');

    window.AdService.Communication.addMethod('adProperties', (data: {slot: string; adType: BreakType}) => {
      consoleLog('adproperties', data);

      if (data.adType === BreakType.video) {
        const container = this.root_.querySelector<HTMLElement>(`[data-service-slot="${data.slot}"]`);

        if (container) {
          const slideIndex = getSlideIndexFromAdContainer(container);

          if (slideIndex) {
            this.model.getSlide(slideIndex).breakType = BreakType.video;
            container.dataset.breakType = BreakType.video;
          }
        }
      }
    });

    window.AdService.Communication.addMethod('videoAdEvent', (data: {slot: string; event: string}) => {
      consoleLog('videoAdEvent', data);

      if (data.event === 'play') {
        window.clearTimeout(this.adSlideTimer);
      } else if (data.event === 'ended') {
        const container = this.root_.querySelector<HTMLElement>(`[data-service-slot="${data.slot}"]`);

        if (container) {
          const slideIndex = getSlideIndexFromAdContainer(container);

          if (slideIndex === this.model.currentSlideIndex && this.model.currentSlide.breakType === BreakType.video) {
            this.adSlideTimer = window.setTimeout((index: number) => {
              if (index === this.model.currentSlideIndex) {
                this.scrollToNextSlide();
              }

              this.adSlideTimer = 0;
            }, 3000, slideIndex);
          }
        }
      }
    });
  }

  private observeSlides() {
    if (!this.observer) {
      consoleWarn('observer not initialized!');
      return;
    }

    this.slideElements.forEach(slide => {
      if (!('observed' in slide.dataset)) {
        slide.dataset.observed = '';
        this.observer.observe(slide);
      }
    });
  }

  private setUpVideoPlayer() {
    consoleLog('set up video player');

    const player = this.root_.querySelector<HTMLVideoElement>('.video-slideshow__overlay .video-slideshow__player');

    if (!player || !this.playerContainer) return;

    this.video = player;
    this.video.poster = this.model.currentSlide.pictureUrl;
    this.playerContainer.style.transform = `translate3d(0, ${this.currentSlideElement.offsetTop + 'px'}, 0)`;
    this.video.src = this.model.currentSlide.videoUrls[1280];
    this.video.muted = this.muted;

    this.video.oncanplay = () => {
      this.firstVideoFrameAvailable = true;
      this.updateView();
      this.sendTrackingPixel('before-play' as TrackingClickName);
    };

    this.video.onclick = () => {
      this.togglePlayPause('video');
    };

    this.video.onloadstart = () => {
      this.playerContainer?.classList.remove('hidden');
      this.sendTrackingPixel('video-start');
    };

    this.video.onplay = () => {
      this.paused = false;
      this.pausedByUser = false;
      this.firstPlayStarted = true;

      this.updateView();

      if (!this.model.currentSlide.playbackStartedPixelFired) {
        this.model.currentSlide.playbackStartedPixelFired = true;

        this.metric.markPlaybackStart();
        this.metric.sendMetricModuleLoadToPlaybackStart();
        this.metric.sendMetricTeaserClickToPlaybackStart();
        this.metric.sendMetricSlideActiveToPlaybackStart();

        this.sendTrackingPixel('video-playback-start');
        this.sendTrackingPixel('content-start');
        this.sendTrackingPixel('content-0-watched');
      }
    };

    this.video.onpause = () => {
      this.paused = this.video.paused;
    };

    this.video.ontimeupdate = () => {
      const position = this.video.currentTime;
      const duration = this.video.duration;

      // tracking for 25%, 50% or 75% watched
      if (!this.model.currentSlide.content25watched && (position >= duration * 0.25)) {
        this.model.currentSlide.content25watched = true;
        this.sendTrackingPixel('content-25-watched');
      }
      if (!this.model.currentSlide.content50watched && (position >= duration * 0.5)) {
        this.model.currentSlide.content50watched = true;
        this.sendTrackingPixel('content-50-watched');
      }
      if (!this.model.currentSlide.content75watched && (position >= duration * 0.75)) {
        this.model.currentSlide.content75watched = true;
        this.sendTrackingPixel('content-75-watched');
      }

      const progressBar = this.currentSlideElement.querySelector('.video-slideshow__progress-bar');
      const progressIndicator = this.currentSlideElement.querySelector<HTMLElement>('.video-slideshow__progress-indicator');

      if (progressBar && progressIndicator) {
        progressIndicator.style.width = (progressBar.clientWidth * position / duration) + 'px';
      }

      // Peek animation
      if (!this.hasPeeked && position >= duration * 0.8) {
        this.hasPeeked = true;

        const overlay = this.root_.querySelector('.video-slideshow__overlay');

        this.currentSlideElement.classList.add('animate');
        this.playerContainer?.classList.add('animate');
        overlay?.classList.remove('snappy');

        window.setTimeout(() => {
          this.root_.querySelector('.animate')?.classList.remove('animate');
          this.playerContainer?.classList.remove('animate');
          overlay?.classList.add('snappy');
        }, 1000);
      }
    };

    this.video.onended = () => {
      this.sendTrackingPixel('content-end');
      this.sendTrackingPixel('video-end');
      this.sendTrackingPixel('content-100-watched');
      this.scrollToNextSlide();
    };

    this.video.onerror = e => {
      consoleError(e);

      this.playerContainer?.classList.add(errorClass);
      this.sendTrackingPixel('video-error');
    };
  }

  private scrollToNextSlide(usedButton?: boolean) {
    const scrollOptions: ScrollIntoViewOptions = {
      behavior: 'smooth'
    };
    this.sendEventPixel('video-slideshow-next', usedButton ? 'video-slideshow__next-button' : undefined);
    if (this.model.totalSlides === this.model.currentSlideIndex + 1) {
      // Last slide reached, scroll back to first slide.
      this.firstSlideElement?.scrollIntoView(scrollOptions);
    } else {
      // There are more slides to see, scroll to next one.
      this.nextSlideElement?.scrollIntoView(scrollOptions);
    }
  }

  private scrollToPrevSlide(usedButton?: boolean) {
    this.prevSlideElement?.scrollIntoView({
      behavior: 'smooth'
    });
    this.sendEventPixel('video-slideshow-prev', usedButton ? 'video-slideshow__prev-button' : undefined);
  }

  closeOverlay() {
    // consoleLog('close');

    document.documentElement.style.removeProperty('overflow-y');

    if (Viewport.isIOSApp()) {
      const wrapper = document.querySelector<HTMLElement>('.wrapper');
      if (wrapper) wrapper.style.removeProperty('display');

      const wrapperElement = document.querySelector<HTMLElement>('.wrapper');
      if (wrapperElement) wrapperElement.style.removeProperty('display');

      const infiniteScrollElement = document.querySelector<HTMLElement>('[data-mod-name="infinitescroll"]');
      if (infiniteScrollElement) infiniteScrollElement.style.removeProperty('display');

      const leftColumnElement = document.querySelector<HTMLElement>('aside.left-col');
      if (leftColumnElement) leftColumnElement.style.removeProperty('display');

      const headerElement = document.querySelector<HTMLElement>('[data-mod-name="header"]');
      if (headerElement) headerElement.style.removeProperty('display');

      document.documentElement.style.removeProperty('background-color');
      document.documentElement.scroll({
        top: this.pageScrollTop,
        behavior: 'instant'
      });
    }

    this.model.resetData();
    this.shown = false;

    window.removeEventListener('blur', this.blurHandler);
    window.removeEventListener('focus', this.focusHandler);
    window.removeEventListener('popstate', this.popStateHandler);
    window.removeEventListener('resize', this.resizeHandler);

    window.clearTimeout(this.adSlideTimer);
    window.clearInterval(this.forceVideoToPlayTimer);

    if (this.video) {
      this.video.oncanplay = null;
      this.video.onclick = null;
      this.video.onpause = null;
      this.video.onloadstart = null;
      this.video.onplay = null;
      this.video.ontimeupdate = null;
      this.video.onended = null;
      this.video.onerror = null;
    }

    this._playerContainer = null;
    this._progressBar = null;

    // Reset AdService parameters to their original ones.
    if (this.isOverlayMode) {
      window.AdService?.setParam('category', window.ui.AdServiceConfig.init.category);
      window.AdService?.setParam('section', window.ui.AdServiceConfig.init.section);
    }

    this.updateView();
    this.sendEventPixel('video-slideshow-close');
  }

  private sendEventPixel(clickname: EventClickName, origin?: string) {
    // consoleLog('send event pixel', clickname);
    this.tracking.sendEventPixel(clickname, this.model.currentSlide, this.model.currentSlideIndex, origin);
  }

  private sendTrackingPixel(clickname: TrackingClickName, origin?: string) {
    try {
      if (this.model.isBreakSlide(this.model.currentSlide)) return;

      this.tracking.sendTrackingPixel(clickname, this.model.currentSlide, this.model.currentSlideIndex, this.video.duration, origin);
    } catch (e) {
      logError(e);
    }
  }

  private updateHistoryState() {
    const currentSlide = this.model.currentSlide;

    if (currentSlide.pageUrl) {
      HistoryService.replaceState({
        headcrumb: currentSlide.headcrumb
      }, currentSlide.pageUrl);
    }
  }

  private toggleDescriptionCollapse(e: Event) {
    const target = e.target as HTMLElement;

    // When in inline mode in desktop, prevent toggling when clicking on anywhere else other than the icon.
    if (this.isInlineMode && isDesktop()) {
      if (!target.classList.contains('video-slideshow__description-toggle-icon')) {
        return;
      }
    }

    const slideElement = (e.target as HTMLElement)?.closest<HTMLElement>('.video-slideshow__slide');

    if (!slideElement) return;

    const collapsibleArea = slideElement.querySelector<HTMLElement>('.video-slideshow__collapsible-area');

    if (!collapsibleArea) return;

    // ignoring clicks if text is short (not expandable)
    if (!elementContentOverflows(collapsibleArea) && slideElement.classList.contains('description-collapsed')) return;

    slideElement.classList.toggle('description-collapsed');
    this.sendEventPixel(
      slideElement.classList.contains('description-collapsed') ? 'description-close' : 'description-open',
      'expand-icon'
    );
  }

  private async playVideo(origin?: 'video' | 'play-icon') {
    try {
      await this.video.play();
      this.sendTrackingPixel('video-play', origin);
    } catch (e) {
      logError(e);
    }
  }

  private togglePlayPause(origin: 'video' | 'play-icon') {
    this.paused = !this.paused;

    if (this.paused) {
      this.video.pause();
      this.pausedByUser = true;
      this.sendTrackingPixel('video-pause', origin);
    } else {
      void this.playVideo(origin);
    }
  }

  private updateProgressBar(offsetX: number, updateVideoTime = false) {
    this.progressBarHoverOffsetX = offsetX;
    this.progressBarHoverVideoTime = this.calculateVideoTimeFromProgressBarPosition(offsetX);

    if (updateVideoTime) {
      this.video.currentTime = this.progressBarHoverVideoTime;
    }

    this.updateView();
  }

  private positionPlayerOnTopOfCurrentSlide() {
    // consoleLog('positionPlayerOnTopOfCurrentSlide', this.playerContainer);
    if (this.playerContainer) {
      this.playerContainer.style.transform = `translate3d(0, ${this.currentSlideElement.offsetTop + 'px'}, 0)`;
    }
  }

  private calculateVideoTimeFromProgressBarPosition(progressBarPosition: number) {
    if (this.progressBar) {
      return Math.max(0, this.video.duration * progressBarPosition / this.progressBar.offsetWidth);
    }

    return 0;
  }

  private renderSlideshow() {
    return this.shown ? html`
      <div class="video-slideshow__overlay snappy ${classMap({muted: this.muted, paused: this.paused, showPlayIcon: !this.firstPlayStarted})}">
        <div class="video-slideshow__overlay-top">
          <button class="video-slideshow__close-button video-slideshow__button" @click="${() => this.isOverlayMode ? window.history.back() : this.closeOverlay()}"></button>
        </div>

        <div class="video-slideshow__navigation-container">
          <div class="video-slideshow__navigation-buttons">
            <button class="video-slideshow__prev-button video-slideshow__button" ?disabled=${this.model.currentSlideIndex === 0} @click="${() => this.scrollToPrevSlide(true)}"></button>
            <button class="video-slideshow__next-button video-slideshow__button" ?disabled=${this.model.currentSlideIndex === this.model.totalSlides - 1} @click="${() => this.scrollToNextSlide(true)}"></button>
          </div>
        </div>

        ${this.renderSlides(this.model.slides)}

        <div class="video-slideshow__player-container hidden">
          <video class="video-slideshow__player" src="" ?autoplay=${this.autoplay} playsinline preload="metadata"></video>

          <div class="video-slideshow__error">
            <div class="video-slideshow__error-icon"></div>
            <div class="video-slideshow__error-message-1">Das Video kann leider nicht abgespielt werden.</div>
            <div class="video-slideshow__error-message-2">Bitte überprüfen Sie Ihre Internetverbindung.</div>
          </div>
        </div>
      </div>` : null;
  }

  private renderSlides(slides: Slide[]) {
    return html`
      ${slides.map((slide: Slide, index: number) => html`
        <div class="video-slideshow__slide description-collapsed"
             data-index="${index}"
             data-type="${slide.type}"
             ?data-last=${index === this.model.totalSlides - 1}>

          ${index >= this.model.currentSlideIndex - 1 && index <= this.model.currentSlideIndex + 1 ? this.renderSlideBackground(slide) : null}

          <div class="video-slideshow__slide-content">
            ${this.model.isBreakSlide(slide) ? html`
              <div class="video-slideshow__labels">
                <div class="teaser__kicker kicker">
                  <span class="kicker__label kicker__label_grey">Anzeige</span>
                </div>
              </div>
              <div class="co-container-container">
                <div class="co-container co-content" data-service-param-tagid="vertical${index > this.slidesBetweenAds ? '_x' : null}"></div>
              </div>
            ` : html`
              ${((index >= this.model.currentSlideIndex - 1 && index <= this.model.currentSlideIndex + 1) && (index !== this.model.currentSlideIndex || !this.firstVideoFrameAvailable)) ? html`
                <img class="video-slideshow__slide-preview-image" src="${slide.pictureUrl}" alt="" />
              ` : null}

              <div class="video-slideshow__overlay-bottom" style="padding-bottom: 0;">
                ${this.isInlineMode || !isDesktop() ? this.renderCollapsibleArea(slide, index, true) : null}

                <div class="video-slideshow__controls">
                  <div class="video-slideshow__playback-icon" @click="${() => this.togglePlayPause('play-icon')}"></div>
                  <div class="video-slideshow__volume-icon" @click="${() => this.muted = !this.muted}"></div>
                </div>

                <div class="video-slideshow__progress-bar"
                     @mousemove="${(e: MouseEvent) => this.updateProgressBar(e.offsetX, e.buttons === 1)}"
                     @click="${(e: MouseEvent) => this.updateProgressBar(e.offsetX, true)}">
                  <div class="video-slideshow__progress-indicator"></div>
                  <div class="video-slideshow__tooltip" style="left: ${clamp(this.progressBarHoverOffsetX, 20, ((this.progressBar?.offsetWidth ?? 0) - 30))}px">
                    ${formatTime(this.progressBarHoverVideoTime)}
                  </div>
                </div>
              </div>
            `}
          </div>

          ${this.isOverlayMode && isDesktop() ? this.renderCollapsibleArea(slide, index) : null}
        </div>
      `)}
    `;
  }

  private renderCollapsibleArea(slide: Slide, slideIndex: number, enableCollapseToggle = false) {
    return html`
      <div class="video-slideshow__collapsible-area" @click=${enableCollapseToggle ? (e: Event) => this.toggleDescriptionCollapse(e) : undefined}>
        ${enableCollapseToggle ? html`<span class="video-slideshow__description-toggle-icon"></span>` : null}

        ${slideIndex === this.model.currentSlideIndex ? html`
          <h1 class="video-slideshow__headline">${slide.headline}</h1>
          ` : html`
          <div class="video-slideshow__headline">${slide.headline}</div>
        `}

        <span class="video-slideshow__description">${slide.caption}</span>
      </div>`;
  }

  private renderSlideBackground(slide: Slide) {
    return this.model.isVideoSlide(slide) ? html`
      <img class="video-slideshow__slide-background" src="${slide.pictureUrl.replace('vertical-w720', 'vertical-w135')}" alt="" />
    ` : html`
      <div class="video-slideshow__slide-background"></div>
    `;
  }
}
